import { ReactNode, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import {
  Chart, ChartArea, ChartCategoryAxis, ChartCategoryAxisItem, ChartLegend, ChartSeries,
  ChartSeriesItem, ChartTooltip, ChartValueAxis, ChartValueAxisItem, Sankey,
} from '@progress/kendo-react-charts';
import type { TooltipContentProps, TooltipContext, SankeyNodeDataItem, SankeyLinkDataItem } from '@progress/kendo-react-charts';

// Shared palette. Every chart reads identity from the same colours: green resolved itself,
// blue took a call, amber needed a person, red needs attention.
const C = {
  app: '#22c55e',
  call: '#3b82f6',
  manual: '#f59e0b',
  idle: '#64748b',
  bad: '#ef4444',
  axis: '#94a3b8',
  grid: 'rgba(148,163,184,0.18)',
};

// Mirrors DispatchConfirmationStatus in LoadOne.Types.
const STATUS_NAMES: Record<number, string> = {
  0: 'Pending', 1: 'Confirmed (App)', 2: 'Confirmed (Call)', 3: 'Call In Progress',
  4: 'Failed', 5: 'Declined', 6: 'Manually Resolved', 7: 'Callback Requested', 8: 'No Answer',
  9: 'Bypassed at Dispatch',
};

const STATUS_ORDER = [1, 2, 6, 9, 0, 3, 7, 8, 5, 4];
const ATTENTION_STATUSES = new Set([4, 5, 7, 8]);

// The dispatcher had the driver on the phone before the load went out, so the agent was never
// asked to do anything. Counted as a dispatch, but kept out of any rate that is meant to read
// as "how well did the automation do", where it would otherwise dilute the result silently.
const BYPASSED_AT_DISPATCH = 9;

function statusColor(status: number): string {
  if (status === 1) return C.app;
  if (status === 2) return C.call;
  if (status === 6) return C.manual;
  if (ATTENTION_STATUSES.has(status)) return C.bad;
  return C.idle;
}

// Sankey tooltips. Kendo takes components rather than a formatter string, so the node and link
// cases are separate rather than branching on a dataType flag.
const SankeyNodeTooltip: React.FC<TooltipContentProps> = ({ dataItem, nodeValue }) => (
  <span>{(dataItem as SankeyNodeDataItem).label?.text}: {nodeValue}</span>
);

const SankeyLinkTooltip: React.FC<TooltipContentProps> = ({ dataItem }) => {
  const link = dataItem as SankeyLinkDataItem;
  return <span>{link.source.label?.text} &rarr; {link.target.label?.text}: {link.value}</span>;
};

// Retell disconnection_reason values, in plain language.
const REASON_LABELS: Record<string, string> = {
  agent_hangup: 'Agent Completed',
  user_hangup: 'Driver Hung Up',
  voicemail_reached: 'Reached Voicemail',
  dial_no_answer: 'No Answer',
  inactivity: 'Inactivity',
  unknown: 'Unknown',
};

function reasonLabel(reason: string | null): string {
  if (!reason) return 'Unknown';
  return REASON_LABELS[reason] ?? reason;
}

function reasonColor(reason: string): string {
  if (reason === 'agent_hangup') return C.call;
  if (reason === 'voicemail_reached') return C.manual;
  if (reason === 'user_hangup' || reason === 'dial_no_answer') return C.bad;
  return C.idle;
}

function rateColor(rate: number): string {
  if (rate >= 85) return C.app;
  if (rate >= 70) return '#eab308';
  return C.bad;
}

function formatMinutes(minutes: number): string {
  if (minutes < 60) return `${Math.round(minutes)} min`;
  return `${(minutes / 60).toFixed(1)} hrs`;
}

function formatDuration(ms: number | null | undefined): string {
  if (ms == null) return '—';
  const s = Math.round(ms / 1000);
  return s < 60 ? `${s}s` : `${Math.floor(s / 60)}m ${s % 60}s`;
}

function usd(value: number | null | undefined, digits = 2): string {
  if (value == null) return '—';
  return `$${value.toFixed(digits)}`;
}

function timeEst(day: string | null, timeUtc: string | null): string {
  if (!day || !timeUtc) return '—';
  return dayjs.utc(`${day}T${timeUtc}`).tz('America/New_York').format('HH:mm');
}

// ---- API shapes ----
export type DispatchConfirmationStatusCount = { Status: number; Count: number };
export type DispatchConfirmationDailyRow = {
  Day: string; Total: number; ConfirmedInApp: number; ConfirmedByCall: number;
  ManuallyResolved: number; Escalated: number;
};
export type DispatchConfirmationTimingRow = { Bucket: string; ConfirmedVia: number; Count: number };
export type DispatchCostSummary = {
  RangeUsd: number; MonthToDateUsd: number; PerConfirmedUsd: number | null; PerCallUsd: number | null;
  NoContactUsd: number; NoContactCalls: number; TotalCalls: number;
};
export type DispatchCallQuality = {
  EndReasons: { Reason: string; Count: number }[];
  MedianDurationMs: number | null; MedianLatencyMs: number | null; LatencyCallCount: number;
};
export type DispatchFlow = {
  Total: number; ConfirmedInApp: number; NoCallNeeded: number; CallPlaced: number;
  Reached: number; NoContact: number; ConfirmedByCall: number; NotConfirmed: number;
};
export type DispatchCallRow = {
  Day: string | null; TimeUtc: string | null; OrderID: number; OrderNumber: number | null;
  CallID: string; AttemptNumber: number | null; DriverNumber: string | null;
  DriverName: string | null; VehicleNumber: string | null; DurationMs: number | null;
  DisconnectionReason: string | null; Outcome: string | null; CallSummary: string | null;
  LatencyE2eP50Ms: number | null; RecordingUrl: string | null;
};

export type DispatchConfirmationSummary = {
  StatusCounts: DispatchConfirmationStatusCount[];
  DailyTrend: DispatchConfirmationDailyRow[];
  AvgCallAttempts: number | null;
  MedianMinutesToConfirm: number | null;
  TimeToConfirmBuckets: string[];
  TimeToConfirmHistogram: DispatchConfirmationTimingRow[];
  Cost: DispatchCostSummary;
  CallQuality: DispatchCallQuality;
  Flow: DispatchFlow;
  Calls: DispatchCallRow[];
};

export type LoadConfirmationAgentStats = { TotalSessions: number };

function countOf(s: DispatchConfirmationSummary, status: number): number {
  return s.StatusCounts.find(r => r.Status === status)?.Count ?? 0;
}
function totalOf(s: DispatchConfirmationSummary): number {
  return s.StatusCounts.reduce((sum, r) => sum + r.Count, 0);
}
function confirmedRate(s: DispatchConfirmationSummary): number {
  // Deliberately not totalOf: a bypassed load never entered the funnel, so counting it here
  // would report the agent as performing worse the more often dispatch handled a load itself.
  const total = totalOf(s) - countOf(s, BYPASSED_AT_DISPATCH);
  if (total === 0) return 0;
  return Math.round(((countOf(s, 1) + countOf(s, 2)) / total) * 1000) / 10;
}

// ---- Layout primitives. Every panel uses the same shell so gutters and heights stay even. ----
function Panel({ title, note, children }: { title: string; note?: ReactNode; children: ReactNode }) {
  return (
    <div className="card h-100 shadow-sm">
      <div className="card-body p-3 d-flex flex-column">
        <div className="d-flex justify-content-between align-items-baseline mb-2">
          <h6 className="text-muted text-uppercase mb-0" style={{ fontSize: '0.7rem', letterSpacing: '0.06em' }}>{title}</h6>
          {note && <small className="text-muted" style={{ fontSize: '0.7rem' }}>{note}</small>}
        </div>
        <div className="flex-grow-1">{children}</div>
      </div>
    </div>
  );
}

function StatCard({ label, value, sub, color }: { label: string; value: string; sub?: ReactNode; color?: string }) {
  return (
    <div className="card h-100 shadow-sm">
      <div className="card-body p-3">
        <div className="text-muted text-uppercase" style={{ fontSize: '0.65rem', letterSpacing: '0.06em' }}>{label}</div>
        <div className="d-flex align-items-baseline mt-1">
          <span className="font-weight-bold" style={{ fontSize: '1.75rem', color: color ?? 'inherit' }}>{value}</span>
          {sub && <span className="text-muted ml-2" style={{ fontSize: '0.75rem' }}>{sub}</span>}
        </div>
      </div>
    </div>
  );
}

// ---- Collapsed card ----
export function DispatchConfirmationCard({ summary, selected, onClick }: {
  summary: DispatchConfirmationSummary; selected: boolean; onClick: () => void;
}) {
  const total = totalOf(summary);
  const escalated = summary.DailyTrend.reduce((sum, r) => sum + r.Escalated, 0);
  const rate = confirmedRate(summary);
  const spend = summary.Cost?.RangeUsd ?? 0;
  // Shown whenever it is non-zero because the rate above is deliberately measured over the
  // other dispatches only, and without this the two numbers cannot be reconciled on sight.
  const bypassed = countOf(summary, BYPASSED_AT_DISPATCH);

  return (
    <div
      className={`card h-100 shadow-sm${selected ? ' border-primary' : ''}`}
      style={{ cursor: 'pointer', ...(selected ? { borderLeft: `4px solid ${C.call}` } : {}) }}
      onClick={onClick}
    >
      <div className="card-body">
        <h6 className="card-title font-weight-bold">Load Confirmation</h6>
        <div className="mb-2">
          <span className="font-weight-bold" style={{ color: rateColor(rate), fontSize: '2rem' }}>{rate}%</span>
          <span className="text-muted ml-1" style={{ fontSize: '0.8rem' }}>confirmed</span>
        </div>
        <div className="text-muted" style={{ fontSize: '0.8rem' }}>
          <strong>{total.toLocaleString()}</strong> dispatches &nbsp;
          <strong>{escalated.toLocaleString()}</strong> escalated
          {bypassed > 0 && <> &nbsp; <strong>{bypassed.toLocaleString()}</strong> bypassed</>}
          {spend > 0 && <> &nbsp; <strong>{usd(spend)}</strong> spend</>}
        </div>
      </div>
    </div>
  );
}

// ---- Agent call log ----
const PAGE_SIZE = 10;

const CALL_FILTERS: { key: string; label: string; match: (r: DispatchCallRow) => boolean }[] = [
  { key: 'all', label: 'All', match: () => true },
  { key: 'confirmed', label: 'Confirmed', match: r => r.Outcome === 'confirmed' },
  { key: 'nocontact', label: 'No Contact', match: r => r.DisconnectionReason === 'voicemail_reached' || r.DisconnectionReason === 'dial_no_answer' },
  { key: 'hangup', label: 'Driver Hung Up', match: r => r.DisconnectionReason === 'user_hangup' },
  { key: 'other', label: 'Declined / Callback', match: r => r.Outcome === 'declined' || r.Outcome === 'callback_requested' },
];

function CallLog({ calls }: { calls: DispatchCallRow[] }) {
  const days = useMemo(() => {
    const set = new Set<string>();
    calls.forEach(r => { if (r.Day) set.add(r.Day); });
    return Array.from(set).sort().reverse();
  }, [calls]);

  const [day, setDay] = useState<string | null>(null);
  const [filter, setFilter] = useState('all');
  const [page, setPage] = useState(0);

  const activeDay = day && days.includes(day) ? day : (days[0] ?? null);
  const activeFilter = CALL_FILTERS.find(f => f.key === filter) ?? CALL_FILTERS[0];

  const rows = useMemo(
    () => calls.filter(r => r.Day === activeDay && activeFilter.match(r)),
    [calls, activeDay, activeFilter]);

  const pageCount = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const safePage = Math.min(page, pageCount - 1);
  const pageRows = rows.slice(safePage * PAGE_SIZE, safePage * PAGE_SIZE + PAGE_SIZE);

  const reset = (fn: () => void) => { fn(); setPage(0); };

  return (
    <div className="card shadow-sm">
      <div className="card-body p-3">
        <div className="d-flex justify-content-between align-items-center flex-wrap mb-2">
          <h6 className="text-muted text-uppercase mb-0" style={{ fontSize: '0.7rem', letterSpacing: '0.06em' }}>Agent Call Log</h6>
          <div className="d-flex align-items-center">
            <div className="btn-group btn-group-sm mr-2">
              {CALL_FILTERS.map(f => (
                <button
                  key={f.key}
                  type="button"
                  className={`btn btn-sm ${filter === f.key ? 'btn-primary' : 'btn-outline-secondary'}`}
                  style={{ fontSize: '0.7rem' }}
                  onClick={() => reset(() => setFilter(f.key))}
                >
                  {f.label}
                </button>
              ))}
            </div>
            <select
              className="form-control form-control-sm"
              style={{ width: 'auto' }}
              value={activeDay ?? ''}
              onChange={e => reset(() => setDay(e.target.value))}
            >
              {days.length === 0 && <option value="">No calls</option>}
              {days.map(d => <option key={d} value={d}>{dayjs(d).format('ddd, MMM D, YYYY')}</option>)}
            </select>
          </div>
        </div>

        {rows.length === 0 ? (
          <p className="text-muted mb-0" style={{ fontSize: '0.85rem' }}>No calls match this filter.</p>
        ) : (
          <>
            <div className="table-responsive">
              <table className="table table-sm table-hover mb-2" style={{ fontSize: '0.8rem' }}>
                <thead>
                  <tr>
                    <th style={{ whiteSpace: 'nowrap' }}>Time</th>
                    <th>Order / Driver</th>
                    <th>Outcome</th>
                    <th>End Reason</th>
                    <th style={{ whiteSpace: 'nowrap' }}>Duration</th>
                    <th style={{ width: '32%' }}>Summary</th>
                    <th className="text-right">Links</th>
                  </tr>
                </thead>
                <tbody>
                  {pageRows.map(r => (
                    <tr key={r.CallID}>
                      <td className="text-nowrap">{timeEst(r.Day, r.TimeUtc)}</td>
                      <td className="text-nowrap">
                        {r.OrderNumber != null
                          ? <a href={`/Track/Index/${r.OrderNumber}`} target="_blank" rel="noopener noreferrer">#{r.OrderNumber}</a>
                          : `#${r.OrderID}`}
                        <div className="text-muted" style={{ fontSize: '0.72rem' }}>
                          {[r.DriverName || r.DriverNumber, r.VehicleNumber].filter(Boolean).join(' · ') || '—'}
                          {r.AttemptNumber != null && r.AttemptNumber > 1 && <> &middot; try {r.AttemptNumber}</>}
                        </div>
                      </td>
                      <td className="text-nowrap">{r.Outcome ?? '—'}</td>
                      <td className="text-nowrap" style={{ color: reasonColor(r.DisconnectionReason ?? '') }}>
                        {reasonLabel(r.DisconnectionReason)}
                      </td>
                      <td className="text-nowrap">{formatDuration(r.DurationMs)}</td>
                      {/* Summaries run to a few hundred characters, so they are clipped to one line
                          and the full text lives in the native tooltip. */}
                      <td>
                        <div
                          title={r.CallSummary ?? ''}
                          style={{ maxWidth: 380, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                        >
                          {r.CallSummary || '—'}
                        </div>
                      </td>
                      <td className="text-right text-nowrap">
                        <a
                          href={`https://dashboard.retellai.com/call-history?call_id=${r.CallID}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          title="Listen to the call in Retell"
                        >
                          Call
                        </a>
                        {r.OrderNumber != null && (
                          <>
                            &nbsp;&middot;&nbsp;
                            <a href={`/Track/Index/${r.OrderNumber}`} target="_blank" rel="noopener noreferrer" title="Open the order in Track">Order</a>
                          </>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="d-flex justify-content-between align-items-center">
              <small className="text-muted">
                {rows.length} call{rows.length === 1 ? '' : 's'} &middot; page {safePage + 1} of {pageCount}
              </small>
              <div className="btn-group btn-group-sm">
                <button type="button" className="btn btn-outline-secondary" disabled={safePage === 0} onClick={() => setPage(safePage - 1)}>Prev</button>
                <button type="button" className="btn btn-outline-secondary" disabled={safePage >= pageCount - 1} onClick={() => setPage(safePage + 1)}>Next</button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

// ---- Detail panel ----
export function DispatchConfirmationDetail({ summary, agent, onClose }: {
  summary: DispatchConfirmationSummary;
  agent: LoadConfirmationAgentStats | null;
  onClose: () => void;
}) {
  const total = totalOf(summary);
  const rate = confirmedRate(summary);
  const escalated = summary.DailyTrend.reduce((sum, r) => sum + r.Escalated, 0);
  // Defaulted rather than assumed: during a deploy the browser can hold the new bundle while
  // the old API is still answering, and these sections would be missing from that payload.
  const cost = summary.Cost ?? { RangeUsd: 0, MonthToDateUsd: 0, PerConfirmedUsd: null, PerCallUsd: null, NoContactUsd: 0, NoContactCalls: 0, TotalCalls: 0 };
  const quality = summary.CallQuality ?? { EndReasons: [], MedianDurationMs: null, MedianLatencyMs: null, LatencyCallCount: 0 };
  const flow = summary.Flow ?? { Total: 0, ConfirmedInApp: 0, NoCallNeeded: 0, CallPlaced: 0, Reached: 0, NoContact: 0, ConfirmedByCall: 0, NotConfirmed: 0 };
  const buckets = summary.TimeToConfirmBuckets ?? [];
  const histogram = summary.TimeToConfirmHistogram ?? [];

  // Resolution funnel: one bar, a segment per status present. Small segments keep their true
  // width but drop their inline label, so the bar never turns into overlapping text.
  const funnel = useMemo(() => STATUS_ORDER
    .map(s => ({ status: s, name: STATUS_NAMES[s], count: countOf(summary, s), color: statusColor(s) }))
    .filter(s => s.count > 0)
    .map(s => ({
      name: s.name,
      type: 'bar' as const,
      stack: { group: 'total' },
      color: s.color,
      data: [s.count],
      labels: {
        visible: total > 0 && s.count / total >= 0.08,
        position: 'center' as const,
        content: () => s.name,
        color: '#fff',
        font: '11px sans-serif',
        background: 'transparent',
        border: { width: 0 },
        padding: 0,
      },
    })), [summary, total]);

  // Confirmation journey. Every stage is counted per order, so each node balances against the
  // one before it rather than mixing per-call and per-order numbers.
  const sankeyData = useMemo(() => {
    const nodes = [
      { id: 'Dispatched', color: C.idle },
      { id: 'Confirmed in App', color: C.app },
      { id: 'Call Placed', color: C.call },
      { id: 'No Call Yet', color: C.idle },
      { id: 'Reached Driver', color: C.call },
      { id: 'No Contact', color: C.manual },
      { id: 'Confirmed by Call', color: C.app },
      { id: 'Not Confirmed', color: C.bad },
    ];
    const links = [
      { sourceId: 'Dispatched', targetId: 'Confirmed in App', value: flow.ConfirmedInApp },
      { sourceId: 'Dispatched', targetId: 'Call Placed', value: flow.CallPlaced },
      { sourceId: 'Dispatched', targetId: 'No Call Yet', value: flow.NoCallNeeded },
      { sourceId: 'Call Placed', targetId: 'Reached Driver', value: flow.Reached },
      { sourceId: 'Call Placed', targetId: 'No Contact', value: flow.NoContact },
      { sourceId: 'Reached Driver', targetId: 'Confirmed by Call', value: flow.ConfirmedByCall },
      { sourceId: 'Reached Driver', targetId: 'Not Confirmed', value: flow.NotConfirmed },
    ].filter(l => l.value > 0);

    const used = new Set<string>();
    links.forEach(l => { used.add(l.sourceId); used.add(l.targetId); });

    return {
      nodes: nodes.filter(n => used.has(n.id)).map(n => ({ ...n, label: { text: n.id } })),
      links,
    };
  }, [flow]);

  // Time to confirm, normalised per channel. App and call volumes differ a lot, so raw counts
  // would just show which channel is busier instead of how each one behaves.
  const timeSeries = useMemo(() => {
    const share = (via: number) => {
      const counts = buckets.map(b =>
        histogram.find(r => r.Bucket === b && r.ConfirmedVia === via)?.Count ?? 0);
      const sum = counts.reduce((a, b) => a + b, 0);
      return counts.map(c => (sum > 0 ? Math.round((c / sum) * 1000) / 10 : 0));
    };
    return [
      { name: 'In App', color: C.app, data: share(1) },
      { name: 'On Call', color: C.call, data: share(2) },
    ];
  }, [buckets, histogram]);

  // Daily trend. The three confirmation channels stack because they are mutually exclusive;
  // escalation is a separate flag that can land on any of them, so it rides as a line.
  const trend = useMemo(() => {
    const days = summary.DailyTrend.map(r => dayjs(r.Day).format('MMM D'));
    const bars = [
      { name: 'In App', key: 'ConfirmedInApp' as const, color: C.app },
      { name: 'On Call', key: 'ConfirmedByCall' as const, color: C.call },
      { name: 'Manual', key: 'ManuallyResolved' as const, color: C.manual },
    ].map(b => ({
      name: b.name,
      type: 'column' as const,
      stack: { group: 'confirmed' },
      color: b.color,
      data: summary.DailyTrend.map(r => r[b.key]),
    }));
    const escalated = summary.DailyTrend.some(r => r.Escalated > 0)
      ? [{
        name: 'Escalated',
        type: 'line' as const,
        style: 'smooth' as const,
        color: C.bad,
        width: 2,
        markers: { size: 6 },
        data: summary.DailyTrend.map(r => r.Escalated),
      }]
      : [];
    return { days, series: [...bars, ...escalated] };
  }, [summary.DailyTrend]);

  // Why calls ended.
  const reasons = useMemo(() => {
    const rows = [...quality.EndReasons].sort((a, b) => a.Count - b.Count);
    return {
      categories: rows.map(r => reasonLabel(r.Reason)),
      data: rows.map(r => ({ count: r.Count, color: reasonColor(r.Reason) })),
    };
  }, [quality]);

  const hasCallData = cost.TotalCalls > 0;

  return (
    <div className="card mt-3 shadow-sm">
      <div className="card-header d-flex align-items-center justify-content-between">
        <div className="d-flex align-items-center">
          <span className="rounded-circle mr-2" style={{ display: 'inline-block', width: 10, height: 10, background: C.call }} />
          <strong>Load Confirmation</strong>
          <span className="text-muted ml-2" style={{ fontSize: '0.85rem' }}>— selected date range</span>
        </div>
        <button type="button" className="close" onClick={onClose}><span>&times;</span></button>
      </div>

      <div className="card-body">
        {total === 0 ? (
          <p className="text-muted mb-0">No data for this period.</p>
        ) : (
          <>
            {/* Headline numbers */}
            <div className="row">
              <div className="col-md-3 mb-3">
                <StatCard label="Total Dispatches" value={total.toLocaleString()} sub={`${escalated} escalated`} />
              </div>
              <div className="col-md-3 mb-3">
                <StatCard label="Auto-Confirmed" value={`${rate}%`} sub="app + call" color={rateColor(rate)} />
              </div>
              <div className="col-md-3 mb-3">
                <StatCard
                  label="Median Time to Confirm"
                  value={summary.MedianMinutesToConfirm != null ? formatMinutes(summary.MedianMinutesToConfirm) : '—'}
                  sub={summary.AvgCallAttempts != null ? `${summary.AvgCallAttempts} avg attempts` : undefined}
                />
              </div>
              <div className="col-md-3 mb-3">
                <StatCard
                  label="Avg Cost / Call"
                  value={usd(cost?.PerCallUsd, 3)}
                  sub={
                    <>
                      <span style={{ fontSize: '0.95rem', fontWeight: 700, color: C.call }}>{usd(cost?.PerConfirmedUsd, 3)}</span>
                      {' '}/ confirmed load
                      {agent && <> &middot; {agent.TotalSessions.toLocaleString()} sessions</>}
                    </>
                  }
                />
              </div>
            </div>

            {/* Composition */}
            <div className="row">
              <div className="col-12 mb-3">
                <Panel title="Resolution Funnel" note="hover a segment for the exact status">
                  <Chart style={{ height: 46 }}>
                    <ChartArea background="transparent" margin={0} />
                    <ChartLegend visible={false} />
                    <ChartTooltip render={({ point }: TooltipContext) => (
                      <span>{point.series.name}: {point.value} ({total > 0 ? ((point.value / total) * 100).toFixed(1) : 0}%)</span>
                    )} />
                    <ChartCategoryAxis>
                      <ChartCategoryAxisItem categories={['']} visible={false} majorGridLines={{ visible: false }} />
                    </ChartCategoryAxis>
                    <ChartValueAxis>
                      <ChartValueAxisItem visible={false} max={total} majorGridLines={{ visible: false }} />
                    </ChartValueAxis>
                    <ChartSeries>
                      {funnel.map(s => <ChartSeriesItem key={s.name} {...s} gap={0.4} />)}
                    </ChartSeries>
                  </Chart>
                </Panel>
              </div>
            </div>

            {/* Journey and speed */}
            <div className="row">
              <div className="col-lg-8 mb-3">
                <Panel title="Confirmation Journey" note="counted per order at every stage">
                  {flow.Total > 0
                    ? (
                      <Sankey
                        style={{ height: 260, width: '100%' }}
                        data={sankeyData}
                        nodes={{ width: 14, padding: 14 }}
                        legend={{ visible: false }}
                        links={{ colorType: 'source', opacity: 0.35 }}
                        labels={{ color: C.axis, font: '11px sans-serif' }}
                        tooltip={{ nodeComponent: SankeyNodeTooltip, linkComponent: SankeyLinkTooltip }}
                      />
                    )
                    : <p className="text-muted mb-0" style={{ fontSize: '0.85rem' }}>No flow data for this period.</p>}
                </Panel>
              </div>
              <div className="col-lg-4 mb-3">
                <Panel title="Time to Confirm" note="share of each channel">
                  <Chart style={{ height: 260 }}>
                    <ChartArea background="transparent" />
                    <ChartTooltip shared={true} />
                    <ChartLegend position="bottom" labels={{ color: C.axis, font: '11px sans-serif' }} />
                    <ChartCategoryAxis>
                      <ChartCategoryAxisItem
                        categories={buckets}
                        labels={{ color: C.axis, font: '10px sans-serif', rotation: 30 }}
                        line={{ color: C.grid }}
                        majorGridLines={{ visible: false }}
                      />
                    </ChartCategoryAxis>
                    <ChartValueAxis>
                      <ChartValueAxisItem
                        labels={{ color: C.axis, font: '10px sans-serif', format: '{0}%' }}
                        line={{ visible: false }}
                        majorGridLines={{ color: C.grid }}
                      />
                    </ChartValueAxis>
                    <ChartSeries>
                      {timeSeries.map(s => (
                        <ChartSeriesItem key={s.name} type="column" name={s.name} color={s.color} data={s.data} tooltip={{ format: '{0}%' }} />
                      ))}
                    </ChartSeries>
                  </Chart>
                </Panel>
              </div>
            </div>

            {/* Trend, cost and call quality */}
            <div className="row">
              <div className="col-lg-8 mb-3">
                <Panel title="Daily Resolution Trend" note="how each day was resolved">
                  <Chart style={{ height: 240 }}>
                    <ChartArea background="transparent" />
                    <ChartTooltip shared={true} />
                    <ChartLegend position="bottom" labels={{ color: C.axis, font: '11px sans-serif' }} />
                    <ChartCategoryAxis>
                      <ChartCategoryAxisItem
                        categories={trend.days}
                        labels={{ color: C.axis, font: '10px sans-serif', rotation: trend.days.length > 10 ? 35 : 0 }}
                        line={{ color: C.grid }}
                        majorGridLines={{ visible: false }}
                      />
                    </ChartCategoryAxis>
                    <ChartValueAxis>
                      <ChartValueAxisItem
                        labels={{ color: C.axis, font: '10px sans-serif' }}
                        line={{ visible: false }}
                        majorGridLines={{ color: C.grid }}
                      />
                    </ChartValueAxis>
                    <ChartSeries>
                      {trend.series.map(s => <ChartSeriesItem key={s.name} {...s} />)}
                    </ChartSeries>
                  </Chart>
                </Panel>
              </div>
              <div className="col-lg-4 mb-3">
                <div className="d-flex flex-column h-100">
                  <div className="mb-3">
                    <Panel title="Unit Economics" note="billed by Retell">
                      <div className="d-flex align-items-baseline">
                        <span className="font-weight-bold" style={{ fontSize: '1.6rem' }}>{usd(cost?.PerCallUsd, 3)}</span>
                        <span className="text-muted ml-2" style={{ fontSize: '0.75rem' }}>avg per call, all outcomes</span>
                      </div>
                      <div className="mt-1">
                        <span className="font-weight-bold" style={{ fontSize: '1.05rem', color: C.call }}>{usd(cost?.PerConfirmedUsd, 3)}</span>
                        <span className="text-muted ml-1" style={{ fontSize: '0.72rem' }}>per confirmed load</span>
                      </div>
                      <div className="d-flex justify-content-between mt-2" style={{ fontSize: '0.78rem' }}>
                        <span className="text-muted">Total spend, all outcomes</span>
                        <strong>{usd(cost?.RangeUsd)}</strong>
                      </div>
                      <div className="d-flex justify-content-between" style={{ fontSize: '0.78rem' }}>
                        <span className="text-muted">Month to date</span>
                        <strong>{usd(cost?.MonthToDateUsd)}</strong>
                      </div>
                      {(cost?.NoContactCalls ?? 0) > 0 && (
                        <div className="d-flex justify-content-between" style={{ fontSize: '0.78rem' }}>
                          <span className="text-muted">Spent reaching nobody</span>
                          <strong style={{ color: C.bad }}>{usd(cost.NoContactUsd)}</strong>
                        </div>
                      )}
                    </Panel>
                  </div>
                  <div className="flex-grow-1">
                    <Panel
                      title="Call End Reasons"
                      note={hasCallData ? `${cost.TotalCalls} calls` : undefined}
                    >
                      {hasCallData ? (
                        <>
                          <Chart style={{ height: 130 }}>
                            <ChartArea background="transparent" />
                            <ChartLegend visible={false} />
                            <ChartTooltip render={({ point }: TooltipContext) => (
                              <span>{String(point.category)}: {point.value}</span>
                            )} />
                            <ChartCategoryAxis>
                              <ChartCategoryAxisItem
                                categories={reasons.categories}
                                labels={{ color: C.axis, font: '11px sans-serif' }}
                                line={{ visible: false }}
                                majorTicks={{ visible: false }}
                                majorGridLines={{ visible: false }}
                              />
                            </ChartCategoryAxis>
                            <ChartValueAxis>
                              <ChartValueAxisItem visible={false} majorGridLines={{ visible: false }} />
                            </ChartValueAxis>
                            <ChartSeries>
                              <ChartSeriesItem
                                type="bar"
                                data={reasons.data}
                                field="count"
                                colorField="color"
                                gap={0.6}
                                labels={{ visible: true, position: 'outsideEnd', color: C.axis, font: '10px sans-serif' }}
                              />
                            </ChartSeries>
                          </Chart>
                          <div className="d-flex justify-content-between border-top pt-2 mt-2" style={{ fontSize: '0.72rem' }}>
                            <span className="text-muted">Median call {formatDuration(quality?.MedianDurationMs)}</span>
                            <span className="text-muted" title={`Measured on the ${quality?.LatencyCallCount ?? 0} calls that reached a real conversation`}>
                              Response {quality?.MedianLatencyMs != null ? `${(quality.MedianLatencyMs / 1000).toFixed(1)}s` : '—'}
                            </span>
                          </div>
                        </>
                      ) : (
                        <p className="text-muted mb-0" style={{ fontSize: '0.85rem' }}>
                          No call detail captured yet for this range.
                        </p>
                      )}
                    </Panel>
                  </div>
                </div>
              </div>
            </div>

            {/* Per-call detail */}
            {hasCallData && <CallLog calls={summary.Calls ?? []} />}
          </>
        )}
      </div>
    </div>
  );
}
